import rest from "@/rest-client";
import createCrudVuexModule from "@/store/crud";

export const organizationQuestionSet = createCrudVuexModule({
  namespaced: true,
  path: "/question-set",
  options: { localizedFields: ["name", "description", "note"] },

  state: {
    isDownloading: false,

    myQuestionSetsPagination: undefined,
    isMyQuestionSetsLoading: false,
    selectedTab: "account",

    organizationQuestionSetsPagination: undefined,
    isOrganizationQuestionSetsLoading: false,
  },
  getters: {
    isDownloading: (state) => state.isDownloading ?? {},

    myQuestionSetsPagination: (state) => state.myQuestionSetsPagination,
    isMyQuestionSetsLoading: (state) => state.isMyQuestionSetsLoading,
    organizationQuestionSetsPagination: (state) => state.organizationQuestionSetsPagination,
    isOrganizationQuestionSetsLoading: (state) => state.isOrganizationQuestionSetsLoading,
    selectedTab: (state) => state.selectedTab,
  },
  mutations: {
    isDownloading: (state, value) => (state.isDownloading = value),

    selectedTab: (state, value) => (state.selectedTab = value),
    myQuestionSetsPagination: (state, value) => (state.myQuestionSetsPagination = value),
    isMyQuestionSetsLoading: (state, value) => (state.isMyQuestionSetsLoading = value),
    organizationQuestionSetsPagination: (state, value) => (state.organizationQuestionSetsPagination = value),
    isOrganizationQuestionSetsLoading: (state, value) => (state.isOrganizationQuestionSetsLoading = value),

    selectedEntity: (state, questionSet) => {
      if (!questionSet) {
        state.selectedEntity = undefined;
        return;
      }

      const conditionals = questionSet.questions.flatMap((i) => i.conditionalQuestions);
      const answers = questionSet.questions.flatMap((i) => i.answers.map((x) => ({ ...x, questionId: i._id }))) ?? [];
      let order = 0;
      questionSet.questions = questionSet.questions.map((question) => {
        question.canDisplay = true;
        const condition = conditionals.find((c) => c.questionId == question.libraryItem);
        if (condition) {
          if (!answers.find((a) => a.optionId == condition.optionId)) {
            question.canDisplay = false;
            // question.answers = [];
          } else {
            order++;
          }
        } else {
          order++;
        }
        question.order = order;
        return question;
      });

      state.selectedEntity = questionSet;
    },
  },
  actions: {
    async attachFile({}, { fileId, questionsetId }) {
      try {
        await rest.post(`/question-set/${questionsetId}/attach-file`, { fileId });
      } finally {
      }
    },
    async addFromLibrary({ commit }, payload) {
      commit("isUpdating", true);
      try {
        const response = await rest.post("/question-set/add-from-library", payload);
        return response;
      } finally {
        commit("isUpdating", false);
      }
    },

    async findById({ commit }, id) {
      commit("isLoading", true);
      try {
        commit("selectedEntity", undefined);
        const response = await rest.get(`/question-set/${id}`);

        commit("selectedEntity", response.data);
        return response;
      } finally {
        commit("isLoading", false);
      }
    },

    async markAnswer({ state, commit }, { question, option, freeTextAnswer, sessionId }) {
      try {
        const answerTypeMultiple = question.answerType == "MULTIPLE";
        const answerTypeFreeText = question.answerType == "FREE_TEXT";
        const o = question.answers.find((i) => i.optionId == option._id);
        if (!answerTypeFreeText) {
          if (answerTypeMultiple) {
            if (!o) {
              question.answers.push({ optionId: option._id });
            } else {
              question.answers = question.answers.filter((i) => i.optionId != option._id);
            }
          } else {
            if (!o) {
              question.answers = [{ optionId: option._id }];
            } else {
              question.answers = [];
            }
          }
        } else {
          question.freeTextAnswer = freeTextAnswer;
        }

        question.answered = true;
        const response = await rest.post(`/question/answer`, { questionId: question._id, freeTextAnswer, optionId: option?._id, sessionId });
        const updatedObject = response.data;
        commit("selectedEntity", {
          ...state.selectedEntity,
          ...updatedObject,
          questions: state.selectedEntity.questions.map((i) => {
            if (i._id == question._id) {
              return question;
            }
            return i;
          }),
        });

        return response;
      } finally {
      }
    },

    async updateNote({ state, commit }, { question, note, sessionId }) {
      try {
        const response = await rest.put(`/question/note`, { questionId: question._id, note, sessionId });

        commit("selectedEntity", {
          ...state.selectedEntity,
          questions: state.selectedEntity.questions.map((i) => {
            if (i._id == question._id) {
              return { ...question, note: response.data.note };
            }
            return i;
          }),
        });

        return response;
      } finally {
      }
    },

    async assign({ state, commit }, { questionSet, account }) {
      try {
        const response = await rest.put(`/question/assign`, { questionSetId: questionSet._id, accountId: account?._id });

        if (state.selectedEntity) {
          commit("selectedEntity", {
            ...state.selectedEntity,
            assignee: account,
          });
        }

        return response;
      } finally {
      }
    },

    async fetchOrganizationQuestionSets({ commit }, pagination = {}, clearStateBeforeRest = false) {
      commit("isOrganizationQuestionSetsLoading", true);
      try {
        if (clearStateBeforeRest) {
          commit("organizationQuestionSetsPagination", undefined);
        }
        const response = await rest.post(`/question-set/organization/paginate`, { pagination });
        commit("organizationQuestionSetsPagination", response.data);
        return response;
      } finally {
        commit("isOrganizationQuestionSetsLoading", false);
      }
    },

    async UpdateAdminNote({ commit, dispatch }, payload) {
      commit("isOrganizationQuestionSetsLoading", true);
      try {
        payload = await dispatch("preparePayload", { editedEntity: {}, form: payload });
        commit("organizationQuestionSetsPagination", undefined);

        const response = await rest.put(`/question-set/admin-note`, payload);

        return response;
      } catch (error) {
      } finally {
        commit("isOrganizationQuestionSetsLoading", false);
      }
    },

    async fetchCurrentUserAssignedQuestionSet({ commit }, pagination = {}, clearStateBeforeRest = false) {
      commit("isMyQuestionSetsLoading", true);
      try {
        if (clearStateBeforeRest) {
          commit("myQuestionSetsPagination", undefined);
        }
        const response = await rest.post(`/question-set/my/paginate`, { pagination });
        commit("myQuestionSetsPagination", response.data);
        return response;
      } finally {
        commit("isMyQuestionSetsLoading", false);
      }
    },

    async download({ commit }, questionSetId) {
      commit("isDownloading", true);
      try {
        const response = await rest.post("/question-set/download", { questionSetId });
        return response;
      } finally {
        commit("isDownloading", false);
      }
    },
  },
});
